import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { getUrl } from 'aws-amplify/storage';
import SocialShare from '../components/SocialShare';
import PaymentGate from '../components/PaymentGate';
import PhotoUpload from '../components/PhotoUpload';
import '../styles/design-system.css';

export default function ProfilePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('about');
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSubscriptionStatus, setUserSubscriptionStatus] = useState('free');
  const [showNameEdit, setShowNameEdit] = useState(false);
  const [newName, setNewName] = useState('');
  const [userPhoto, setUserPhoto] = useState(null);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);

  // Personality type descriptions for travel matching
  const personalityDescriptions = {
    'Adventure Seeker': 'You thrive on spontaneity and new experiences. Perfect for travelers who want to explore off-the-beaten-path destinations and embrace the unexpected.',
    'Cultural Explorer': 'You love immersing yourself in local cultures and traditions. Ideal for travelers who want authentic experiences and meaningful connections.',
    'Relaxation Enthusiast': 'You prefer laid-back, peaceful travel experiences. Great for travelers who want to unwind and enjoy serene destinations.',
    'Social Butterfly': 'You love meeting new people and group activities. Perfect for travelers who want to make friends and share experiences.',
    'Independent Wanderer': 'You enjoy solo exploration and personal discovery. Ideal for travelers who want flexibility and self-guided adventures.',
    'Luxury Traveler': 'You appreciate comfort and premium experiences. Great for travelers who want high-quality accommodations and services.',
    'Budget Conscious': 'You\'re smart about spending and love finding great deals. Perfect for travelers who want value and affordability.',
    'Family Oriented': 'You prioritize family-friendly activities and safe environments. Ideal for travelers with children or who prefer family-focused trips.'
  };

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoading(true);
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        console.log('Current user:', currentUser);
        console.log('User attributes:', attributes);
        
        // Get the user's actual name from Cognito attributes
        let displayName = attributes.name || attributes['custom:name'];
        
        // If no name is set, extract from email as fallback
        if (!displayName || displayName === 'User') {
          const email = attributes.email || currentUser.username;
          if (email && email.includes('@')) {
            const emailName = email.split('@')[0];
            // Convert to proper case (e.g., "samnight777" -> "Sam")
            displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1).replace(/[0-9]/g, '');
            if (displayName.length < 2) {
              displayName = emailName.charAt(0).toUpperCase() + emailName.slice(1, 3);
            }
          } else {
            displayName = 'User';
          }
        }
        
        console.log('Display name determined:', displayName);
        
        // Try to get user's photo from S3 if they have one
        let photoUrl = '/default-avatar.png';
        if (attributes.picture) {
          try {
            photoUrl = await getUrl({
              key: attributes.picture,
              options: {
                accessLevel: 'guest',
                validateObjectExistence: false
              }
            });
          } catch (err) {
            console.log('Could not load user photo, using default:', err.message);
          }
        }
        
        // Get personality data from attributes
        const personalityScore = attributes['custom:personalityScore'] || null;
        const personalityType = attributes['custom:personalityType'] || null;
        const personalityDescription = personalityType ? personalityDescriptions[personalityType] : null;
        
        // Create user profile from Cognito attributes
        const profile = {
          id: currentUser.userId,
          username: attributes['custom:username'] || attributes.username || currentUser.username,
          name: displayName,
          email: attributes.email || currentUser.username,
          phone: attributes.phone_number || '',
          city: attributes['custom:city'] || '',
          country: attributes['custom:country'] || '',
          ageGroup: attributes['custom:ageGroup'] || '',
          photo: photoUrl,
          bio: personalityDescription || 'Welcome to your GradedWash profile! Complete your personality test to get better matches.',
          interests: [],
          personality: personalityType || 'Not completed yet',
          travelStyle: 'Tell us about your travel style!',
          languages: ['English'],
          countriesVisited: 0,
          personalityScore: personalityScore,
          personalityType: personalityType,
          personalityDescription: personalityDescription,
          upcomingTrips: [],
          pastTrips: []
        };

        console.log('Created profile:', profile);
        setUserProfile(profile);
        setUserPhoto(photoUrl);
        setNewName(profile.name);
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const handlePersonalityTest = () => {
    navigate('/personality-test');
  };

  const handleUpdateName = async () => {
    try {
      await updateUserAttributes({
        userAttributes: {
          name: newName
        }
      });
      
      setUserProfile(prev => ({
        ...prev,
        name: newName
      }));
      setShowNameEdit(false);
    } catch (err) {
      console.error('Error updating name:', err);
      setError('Failed to update name');
    }
  };

  const handlePhotoUploaded = async (url, key) => {
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:picture': key
        }
      });
      
      setUserPhoto(url);
      setUserProfile(prev => ({
        ...prev,
        photo: url
      }));
      setShowPhotoUpload(false);
    } catch (err) {
      console.error('Error updating photo:', err);
      setError('Failed to update photo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="alert alert-error mb-4">{error}</div>
          <button onClick={() => window.location.reload()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Profile Header */}
      <section className="bg-white shadow-sm border-b border-gray-200">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative">
              <img 
                src={userPhoto} 
                alt={userProfile.name}
                className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-lg"
              />
              <button 
                onClick={() => setShowPhotoUpload(true)}
                className="btn btn-secondary absolute bottom-0 right-0 text-xs px-2 py-1"
              >
                Edit
              </button>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-3xl font-bold text-gray-900">{userProfile.name}</h1>
                <button 
                  onClick={() => setShowNameEdit(true)}
                  className="btn btn-secondary text-sm"
                >
                  Edit Name
                </button>
              </div>
              
              {showNameEdit && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter your name"
                      className="form-input flex-1"
                    />
                    <button 
                      onClick={handleUpdateName}
                      className="btn btn-primary"
                    >
                      Save
                    </button>
                    <button 
                      onClick={() => setShowNameEdit(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              <p className="text-gray-600 mb-4">
                {userProfile.email}
                {userProfile.city && ` • ${userProfile.city}`}
                {userProfile.country && `, ${userProfile.country}`}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {userProfile.personalityScore ? (
                  <span className="bg-success text-white px-3 py-1 rounded-full text-sm font-medium">
                    {userProfile.personalityScore}% Match Score
                  </span>
                ) : (
                  <span className="bg-warning text-white px-3 py-1 rounded-full text-sm font-medium">
                    Take Personality Test
                  </span>
                )}
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                  {userProfile.countriesVisited} Countries
                </span>
                {userSubscriptionStatus === 'free' && (
                  <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Free Plan
                  </span>
                )}
                {userSubscriptionStatus === 'paid' && (
                  <span className="bg-success text-white px-3 py-1 rounded-full text-sm font-medium">
                    Premium Member
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex flex-col gap-3">
              <PaymentGate feature="search" userSubscriptionStatus={userSubscriptionStatus}>
                <Link to="/feed" className="btn btn-primary">
                  Find Travel Companions
                </Link>
              </PaymentGate>
              <button 
                onClick={handlePersonalityTest}
                className="btn btn-secondary"
              >
                {userProfile.personalityScore ? 'Retake Personality Test' : 'Take Personality Test'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Personality Match Section */}
      {userProfile.personalityScore && (
        <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-8">
          <div className="container">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold mb-2">Your Travel Personality</h2>
              <p className="text-xl opacity-90">This helps other travelers understand your travel style</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-4xl mx-auto">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">{userProfile.personalityType}</h3>
                <div className="text-4xl font-bold mb-4">{userProfile.personalityScore}%</div>
                <p className="text-lg opacity-90">Match Score</p>
              </div>
              
              {userProfile.personalityDescription && (
                <div className="bg-white/20 rounded-lg p-4">
                  <p className="text-lg leading-relaxed">{userProfile.personalityDescription}</p>
                </div>
              )}
              
              <div className="text-center mt-6">
                <button 
                  onClick={handlePersonalityTest}
                  className="btn btn-secondary"
                >
                  Update Your Personality Profile
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Welcome Dashboard Section */}
      <div className="container py-8">
        <div className="card p-8 bg-gradient-to-br from-gray-50 to-gray-100">
          <h2 className="text-2xl font-bold mb-4">Welcome, {userProfile.name}! Here's your GradedWash Dashboard:</h2>
          
          {userProfile.personalityScore ? (
            <div className="text-lg text-gray-700 mb-6">
              <strong>You're a {userProfile.personalityType}!</strong><br/>
              Your personality match score helps others see how well you'll get along, making every meet-up and trip more enjoyable.
            </div>
          ) : (
            <div className="text-lg text-gray-700 mb-6">
              <strong>Complete your personality test to get started!</strong><br/>
              This will help you find compatible travel companions and make your profile more attractive to other travelers.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card p-6 text-center">
              <div className="text-3xl mb-2">🧠</div>
              <h3 className="text-lg font-semibold mb-2">Personality Match</h3>
              <p className="text-gray-600 text-sm">
                {userProfile.personalityScore ? 
                  `${userProfile.personalityScore}% compatibility score` : 
                  'Take the test to get your score'
                }
              </p>
            </div>
            
            <div className="card p-6 text-center">
              <div className="text-3xl mb-2">🌍</div>
              <h3 className="text-lg font-semibold mb-2">Travel Companions</h3>
              <p className="text-gray-600 text-sm">
                Find like-minded travelers for your next adventure
              </p>
            </div>
            
            <div className="card p-6 text-center">
              <div className="text-3xl mb-2">💬</div>
              <h3 className="text-lg font-semibold mb-2">Messages</h3>
              <p className="text-gray-600 text-sm">
                Connect with potential travel partners
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Upload Modal */}
      {showPhotoUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Update Profile Photo</h3>
              <button 
                onClick={() => setShowPhotoUpload(false)}
                className="text-2xl hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <PhotoUpload 
              onPhotoUploaded={handlePhotoUploaded}
              currentPhoto={userPhoto}
            />
          </div>
        </div>
      )}

      {/* Profile Content */}
      <section className="p-6">
        <div className="container">
          <div className="row">
            {/* Main Content */}
            <div className="col col--8">
              {/* Tabs */}
              <div className="d-flex" style={{ marginBottom: 'var(--spacing-6)', borderBottom: '1px solid var(--gray-200)' }}>
                <button
                  onClick={() => setActiveTab('about')}
                  className={`btn ${activeTab === 'about' ? 'btn--primary' : 'btn--secondary'}`}
                  style={{ borderRadius: '0', borderBottom: activeTab === 'about' ? '2px solid var(--primary-blue)' : 'none' }}
                >
                  About
                </button>
                <button
                  onClick={() => setActiveTab('trips')}
                  className={`btn ${activeTab === 'trips' ? 'btn--primary' : 'btn--secondary'}`}
                  style={{ borderRadius: '0', borderBottom: activeTab === 'trips' ? '2px solid var(--primary-blue)' : 'none' }}
                >
                  Trips
                </button>
                <button
                  onClick={() => setActiveTab('photos')}
                  className={`btn ${activeTab === 'photos' ? 'btn--primary' : 'btn--secondary'}`}
                  style={{ borderRadius: '0', borderBottom: activeTab === 'photos' ? '2px solid var(--primary-blue)' : 'none' }}
                >
                  Photos
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'about' && (
                <div>
                  <div className="card p-6" style={{ marginBottom: 'var(--spacing-6)' }}>
                    <h3>About Me</h3>
                    <p style={{ lineHeight: 1.6 }}>{userProfile.bio}</p>
                    <p style={{ lineHeight: 1.6, color: 'var(--gray-600)' }}>
                      <strong>Username:</strong> {userProfile.username}<br/>
                      <strong>Email:</strong> {userProfile.email}<br/>
                      <strong>Location:</strong> {userProfile.city}, {userProfile.country}<br/>
                      <strong>Age Group:</strong> {userProfile.ageGroup}
                    </p>
                  </div>

                  <div className="card p-6" style={{ marginBottom: 'var(--spacing-6)' }}>
                    <h3>Travel Style</h3>
                    <p style={{ lineHeight: 1.6 }}>{userProfile.travelStyle}</p>
                  </div>

                  <div className="card p-6">
                    <h3>Personality Traits</h3>
                    {userProfile.personalityScore ? (
                      <div className="d-flex" style={{ gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                        {userProfile.personality.split(', ').map(trait => (
                          <span key={trait} className="bg-light text-muted" style={{
                            padding: 'var(--spacing-2) var(--spacing-3)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-sm)'
                          }}>
                            {trait}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: 'var(--gray-600)' }}>
                        Complete your personality test to see your traits and get better matches!
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'trips' && (
                <div>
                  <div className="card p-6" style={{ marginBottom: 'var(--spacing-6)' }}>
                    <h3>Upcoming Trips</h3>
                    {userProfile.upcomingTrips.length > 0 ? (
                      userProfile.upcomingTrips.map((trip, index) => (
                        <div key={index} className="d-flex align-center justify-between p-4" style={{ 
                          borderBottom: index < userProfile.upcomingTrips.length - 1 ? '1px solid var(--gray-200)' : 'none'
                        }}>
                          <div>
                            <h4 style={{ margin: 0, marginBottom: 'var(--spacing-1)' }}>{trip.destination}</h4>
                            <p className="text-muted" style={{ margin: 0 }}>{trip.dates}</p>
                          </div>
                          <span className={`${trip.status === 'Confirmed' ? 'bg-success' : 'bg-warning'} text-white`} style={{
                            padding: 'var(--spacing-1) var(--spacing-3)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'bold'
                          }}>
                            {trip.status}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: 'var(--gray-600)' }}>No upcoming trips planned yet.</p>
                    )}
                  </div>

                  <div className="card p-6">
                    <h3>Past Trips</h3>
                    {userProfile.pastTrips.length > 0 ? (
                      userProfile.pastTrips.map((trip, index) => (
                        <div key={index} className="p-4" style={{ 
                          borderBottom: index < userProfile.pastTrips.length - 1 ? '1px solid var(--gray-200)' : 'none'
                        }}>
                          <h4 style={{ margin: 0, marginBottom: 'var(--spacing-1)' }}>{trip.destination}</h4>
                          <p className="text-muted" style={{ margin: 0, marginBottom: 'var(--spacing-2)' }}>{trip.dates}</p>
                          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                            <strong>Highlights:</strong> {trip.highlights}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: 'var(--gray-600)' }}>No past trips recorded yet.</p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'photos' && (
                <div className="card p-6">
                  <h3>Travel Photos</h3>
                  <p className="text-muted">Photos will be displayed here when available.</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col col--4">
              <div className="card p-6" style={{ marginBottom: 'var(--spacing-6)' }}>
                <h3>Interests</h3>
                {userProfile.interests.length > 0 ? (
                  <div className="d-flex" style={{ gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                    {userProfile.interests.map(interest => (
                      <span key={interest} className="bg-primary text-white" style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)'
                      }}>
                        {interest}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--gray-600)' }}>Add your interests to get better matches!</p>
                )}
              </div>

              <div className="card p-6" style={{ marginBottom: 'var(--spacing-6)' }}>
                <h3>Languages</h3>
                <div className="d-flex" style={{ gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                  {userProfile.languages.map(language => (
                    <span key={language} className="bg-light text-muted" style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      {language}
                    </span>
                  ))}
                </div>
              </div>

              <div className="card p-6">
                <h3>Travel Stats</h3>
                <div className="row text-center">
                  <div className="col col--6">
                    <div className="bg-primary text-white" style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto var(--spacing-2)',
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: 'bold'
                    }}>
                      {userProfile.countriesVisited}
                    </div>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Countries</p>
                  </div>
                  <div className="col col--6">
                    <div className="bg-success text-white" style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto var(--spacing-2)',
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: 'bold'
                    }}>
                      {userProfile.pastTrips.length}
                    </div>
                    <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>Trips</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

